import { create } from "zustand";
import {
  type AnyChangeLedgerItem,
  ChangeType,
  RedoChange,
  UndoChange,
  UnknownChange,
  UpdateClassesChange,
  UpdateStoryboardsChange,
  UpdatePageChange,
  CreatePageChange,
  DeletePageChange,
  AddLibraryComponentChange,
  RemoveLibraryComponentChange,
} from "./changeLedgerTypes";
import { useCodebaseStore } from "./codebaseStore";
import type { SignedInSessionResource } from "@clerk/types";
import axios from "axios";
import type { JsxElement } from "@/utils/jsx/jsxElementTypes";
import { useEffect } from "react";
import { toast } from "sonner";
import useActivityStore, { type ActivityItem } from "./activityStore";
import { ActivityChangeTypeToName } from "@/components/ActivityStream/ActivityStreamItem";
import { getAxiosErorrMessage } from "@/utils/utils";
import { track } from "@/utils/events/tracker";
import { CHANGE_TYPES_WITH_INSTANT_UPDATE } from "@/components/Canvas/Storyboard/StaticIframe/hooks/useApplyInstantUpdates";
import { useDatabaseSyncWrapperStore } from "@/components/contexts/databaseSyncStore";
import { usePagesStore } from "./pagesStore";
import { v4 as uuidv4 } from "uuid";

export const APPLIED_BUT_NOT_CONFIRMED_BY_API_CONSTANT = -1;
export const FAILED_CONSTANT = -2;
const TIME_BEFORE_FLUSHING_CHANGES = 3_000;

const getDisplayNameForItem = (
  ledgerItem: AnyChangeLedgerItem,
  activities: ActivityItem[],
) => {
  if (!ledgerItem) {
    return "";
  }

  if (ledgerItem.type === ChangeType.UNKNOWN) {
    const matchingActivity = activities.find(
      (activity) => activity.id === ledgerItem.activityId,
    );
    if (matchingActivity) {
      return ActivityChangeTypeToName[matchingActivity.change_type];
    }
  }

  return ledgerItem.changeName;
};

const applyChangeToStore = (
  change: AnyChangeLedgerItem,
  get: () => DesignCommandManagerStore,
  set: (
    updateFunction: (
      state: DesignCommandManagerStore,
    ) => Partial<DesignCommandManagerStore>,
  ) => void,
) => {
  if (change.type === ChangeType.UPDATE_CLASSES) {
    const updateClassesChange = change as UpdateClassesChange;
    const codebaseId = updateClassesChange.changeFields.codebaseIdToUpdateClass;

    set((state) => {
      return {
        editingClassesByCodebaseId: {
          ...state.editingClassesByCodebaseId,
          [codebaseId]: {
            value: updateClassesChange.changeFields.newClasses,
            lastManualUpdateAt: new Date(),
          },
        },
      };
    });
  } else if (change.type === ChangeType.UPDATE_STORYBOARD) {
    const updateStoryboardsChange = change as UpdateStoryboardsChange;
    const canvasId = updateStoryboardsChange.changeFields.canvasId;

    // Store pagesStore undo data for potential undo and apply deltas to pagesStore
    const pagesStore = usePagesStore.getState();
    const selectedPageId = pagesStore.selectedPageId[canvasId];

    for (const delta of updateStoryboardsChange.changeFields.deltas) {
      if (delta.pageConfigPath) {
        const pages = pagesStore.pages[canvasId];
        if (pages) {
          const currentPage = pages.find((p) => p.id === selectedPageId);
          if (currentPage && currentPage.configPath === delta.pageConfigPath) {
            const prevUndoData =
              change.instantUpdateData?.pagesStoreUndoData || [];

            // Store current state for undo
            change.instantUpdateData = {
              ...(change.instantUpdateData || {}),
              pagesStoreUndoData: [
                ...prevUndoData,
                {
                  canvasId,
                  pageId: selectedPageId,
                  previousStoryboards: currentPage.storyboards || [],
                },
              ],
            };

            // Apply the delta to pagesStore instantly
            const currentStoryboards = currentPage.storyboards || [];
            let newStoryboards = [...currentStoryboards];

            if (delta.changeType === "upsert") {
              const existingIndex = newStoryboards.findIndex(
                (sb) => sb.id === delta.storyboardId,
              );
              if (existingIndex !== -1) {
                // Update existing storyboard
                newStoryboards[existingIndex] = {
                  ...newStoryboards[existingIndex],
                  ...delta.storyboardUpdateDict,
                };
              } else {
                // Add new storyboard
                newStoryboards.push(delta.storyboardUpdateDict as any);
              }
            } else if (delta.changeType === "update") {
              const existingIndex = newStoryboards.findIndex(
                (sb) => sb.id === delta.storyboardId,
              );
              if (existingIndex !== -1) {
                newStoryboards[existingIndex] = {
                  ...newStoryboards[existingIndex],
                  ...delta.storyboardUpdateDict,
                };
              }
            } else if (delta.changeType === "remove") {
              newStoryboards = newStoryboards.filter(
                (sb) => sb.id !== delta.storyboardId,
              );
            }

            // Update the page in pagesStore
            if (selectedPageId) {
              pagesStore.updatePage(canvasId, selectedPageId, {
                storyboards: newStoryboards,
              });

              // Track this activity as pending to avoid incoming page updates overwriting the change
              // null = awaiting API response, will be updated with real timestamp after API confirms
              if (change.activityId) {
                pagesStore.addPendingPageActivity(
                  selectedPageId,
                  change.activityId,
                );
              }
            }
          }
        }
      }
    }
  } else if (change.type === ChangeType.UPDATE_PAGE) {
    const updatePageChange = change as UpdatePageChange;
    const pagesStore = usePagesStore.getState();
    const { canvasId, pageId } = updatePageChange.changeFields;

    // Apply instant update to pagesStore
    pagesStore.updatePage(
      canvasId,
      pageId,
      updatePageChange.changeFields.updates,
    );
  } else if (change.type === ChangeType.CREATE_PAGE) {
    const createPageChange = change as CreatePageChange;
    const pagesStore = usePagesStore.getState();
    const { canvasId, page, configPath } = createPageChange.changeFields;

    // Extract page properties with defaults
    const pageId = page?.id || uuidv4();
    const pageName = page?.name || "Untitled Page";
    const pageConfigPath = configPath || "";

    // Create new page in pagesStore with configPath
    const newPage = {
      id: pageId,
      name: pageName,
      storyboards: (page?.storyboards || []) as any, // Cast to match Page type
      configPath: pageConfigPath,
      folderPath: pageConfigPath.split("/").slice(0, -1).join("/"), // Derive folderPath from configPath
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    pagesStore.addPage(canvasId, newPage as any);

    // Store for undo
    change.instantUpdateData = {
      ...(change.instantUpdateData || {}),
      createdPageId: pageId,
    };
  } else if (change.type === ChangeType.DELETE_PAGE) {
    const deletePageChange = change as DeletePageChange;
    const pagesStore = usePagesStore.getState();
    const { canvasId, pageId } = deletePageChange.changeFields;

    // Store page data for undo
    const pages = pagesStore.pages[canvasId] || [];
    const pageToDelete = pages.find((p) => p.id === pageId);
    const selectedPageId = pagesStore.selectedPageId[canvasId];
    const newSelectedPageId =
      pageId === selectedPageId
        ? pages.filter((p) => p.id !== pageId)[0]?.id
        : selectedPageId;

    if (pageToDelete) {
      change.instantUpdateData = {
        ...(change.instantUpdateData || {}),
        deletedPageData: { pageToDelete, selectedPageId },
      };

      // Remove from pagesStore
      pagesStore.removePage(canvasId, pageId);
      pagesStore.setSelectedPageId(canvasId, newSelectedPageId || null);
    }
  } else if (change.type === ChangeType.ADD_LIBRARY_COMPONENT) {
    const addLibChange = change as AddLibraryComponentChange;
    const { componentPath, componentName, libraryId } =
      addLibChange.changeFields;

    const codebaseStore = useCodebaseStore.getState();
    const currentLibraries = (codebaseStore.conditions?.config?.data
      ?.libraries || []) as any[];

    const newComponent: { importPath: string; importName?: string } = {
      importPath: componentPath,
    };
    if (componentName) newComponent.importName = componentName;

    const existingLibrary = currentLibraries.find(
      (lib: any) => lib.id === libraryId,
    );

    const newLibraries = existingLibrary
      ? currentLibraries.map((lib: any) =>
          lib.id === libraryId
            ? { ...lib, components: [...lib.components, newComponent] }
            : lib,
        )
      : [
          ...currentLibraries,
          { id: libraryId, name: "default", components: [newComponent] },
        ];

    codebaseStore.setConditionsLibraries(newLibraries);
  } else if (change.type === ChangeType.REMOVE_LIBRARY_COMPONENT) {
    const removeLibChange = change as RemoveLibraryComponentChange;
    const { componentPath, componentName, libraryId } =
      removeLibChange.changeFields;

    const codebaseStore = useCodebaseStore.getState();
    const currentLibraries = (codebaseStore.conditions?.config?.data
      ?.libraries || []) as any[];

    const componentMatches = (c: any) =>
      c.importPath === componentPath &&
      (componentName ? c.importName === componentName : !c.importName);

    // Remove component from matching library, then filter out empty libraries
    const newLibraries = currentLibraries
      .map((lib: any) =>
        lib.id === libraryId
          ? {
              ...lib,
              components: lib.components.filter(
                (c: any) => !componentMatches(c),
              ),
            }
          : lib,
      )
      .filter((lib: any) => lib.components && lib.components.length > 0);

    codebaseStore.setConditionsLibraries(newLibraries);
  }
};

const undoChangeInStore = (
  changeToUndo: AnyChangeLedgerItem,
  get: () => DesignCommandManagerStore,
  set: (
    updateFunction: (
      state: DesignCommandManagerStore,
    ) => Partial<DesignCommandManagerStore>,
  ) => void,
) => {
  if (changeToUndo.type === ChangeType.UPDATE_CLASSES) {
    const updateClassesChange = changeToUndo as UpdateClassesChange;
    const codebaseId = updateClassesChange.changeFields.codebaseIdToUpdateClass;

    set((state) => {
      return {
        editingClassesByCodebaseId: {
          ...state.editingClassesByCodebaseId,
          [codebaseId]: {
            value: updateClassesChange.changeFields.oldClasses,
            lastManualUpdateAt: new Date(),
          },
        },
      };
    });
  } else if (changeToUndo.type === ChangeType.UPDATE_STORYBOARD) {
    // Undo pagesStore changes instantly
    const pagesStoreUndoData =
      changeToUndo.instantUpdateData?.pagesStoreUndoData;

    if (pagesStoreUndoData) {
      for (const undoData of pagesStoreUndoData) {
        const { canvasId, pageId, previousStoryboards } = undoData;
        const pagesStore = usePagesStore.getState();

        // Restore previous storyboards in pagesStore
        pagesStore.updatePage(canvasId, pageId, {
          storyboards: previousStoryboards,
        });
      }
    }
  } else if (changeToUndo.type === ChangeType.UPDATE_PAGE) {
    const updatePageChange = changeToUndo as UpdatePageChange;
    const pagesStore = usePagesStore.getState();
    const { canvasId, pageId, previousValues } = updatePageChange.changeFields;

    // Restore previous values in pagesStore
    pagesStore.updatePage(canvasId, pageId, previousValues);
  } else if (changeToUndo.type === ChangeType.CREATE_PAGE) {
    const createPageChange = changeToUndo as CreatePageChange;
    const { canvasId } = createPageChange.changeFields;
    const pageId = createPageChange.instantUpdateData?.createdPageId;

    if (pageId) {
      // Do both page removal AND selection update in a single atomic state update
      // to prevent intermediate state where selectedPageId is null
      usePagesStore.setState((state) => {
        const currentPages = state.pages[canvasId] || [];
        const remainingPages = currentPages.filter((p) => p.id !== pageId);

        return {
          pages: { ...state.pages, [canvasId]: remainingPages },
          selectedPageId: {
            ...state.selectedPageId,
            [canvasId]: remainingPages[0]?.id || null,
          },
        };
      });
    }
  } else if (changeToUndo.type === ChangeType.DELETE_PAGE) {
    const deletePageChange = changeToUndo as DeletePageChange;
    const pagesStore = usePagesStore.getState();
    const { canvasId } = deletePageChange.changeFields;
    const { deletedPageData, selectedPageId } =
      deletePageChange.instantUpdateData?.deletedPageData;

    if (deletedPageData) {
      pagesStore.addPage(canvasId, deletedPageData);
      pagesStore.setSelectedPageId(canvasId, selectedPageId);
    }
  } else if (changeToUndo.type === ChangeType.ADD_LIBRARY_COMPONENT) {
    // Undo add = remove the component
    const addLibChange = changeToUndo as AddLibraryComponentChange;
    const { componentPath, componentName, libraryId } =
      addLibChange.changeFields;

    const codebaseStore = useCodebaseStore.getState();
    const currentLibraries = (codebaseStore.conditions?.config?.data
      ?.libraries || []) as any[];

    const componentMatches = (c: any) =>
      c.importPath === componentPath &&
      (componentName ? c.importName === componentName : !c.importName);

    // Remove component from matching library, then filter out empty libraries
    const newLibraries = currentLibraries
      .map((lib: any) =>
        lib.id === libraryId
          ? {
              ...lib,
              components: lib.components.filter(
                (c: any) => !componentMatches(c),
              ),
            }
          : lib,
      )
      .filter((lib: any) => lib.components && lib.components.length > 0);

    codebaseStore.setConditionsLibraries(newLibraries);
  } else if (changeToUndo.type === ChangeType.REMOVE_LIBRARY_COMPONENT) {
    // Undo remove = re-add the component
    const removeLibChange = changeToUndo as RemoveLibraryComponentChange;
    const { componentPath, componentName, libraryId } =
      removeLibChange.changeFields;

    const codebaseStore = useCodebaseStore.getState();
    const currentLibraries = (codebaseStore.conditions?.config?.data
      ?.libraries || []) as any[];

    const newComponent: { importPath: string; importName?: string } = {
      importPath: componentPath,
    };
    if (componentName) newComponent.importName = componentName;

    // Add component back to matching library, or recreate library if it was deleted
    const existingLibrary = currentLibraries.find(
      (lib: any) => lib.id === libraryId,
    );

    const newLibraries = existingLibrary
      ? currentLibraries.map((lib: any) =>
          lib.id === libraryId
            ? { ...lib, components: [...lib.components, newComponent] }
            : lib,
        )
      : [
          ...currentLibraries,
          { id: libraryId, name: "default", components: [newComponent] },
        ];

    codebaseStore.setConditionsLibraries(newLibraries);
  }
};

const redoChangeInStore = (
  change: AnyChangeLedgerItem,
  get: () => DesignCommandManagerStore,
  set: (
    updateFunction: (
      state: DesignCommandManagerStore,
    ) => Partial<DesignCommandManagerStore>,
  ) => void,
) => {
  if (change.type === ChangeType.UPDATE_CLASSES) {
    // Simply apply the change again
    applyChangeToStore(change, get, set);
  } else if (change.type === ChangeType.UPDATE_STORYBOARD) {
    // Apply the storyboard change again (pagesStore will be updated by instant update)
    applyChangeToStore(change, get, set);
  } else if (change.type === ChangeType.UPDATE_PAGE) {
    // Apply the page change again
    applyChangeToStore(change, get, set);
  } else if (change.type === ChangeType.CREATE_PAGE) {
    // Apply the page creation again
    applyChangeToStore(change, get, set);
  } else if (change.type === ChangeType.DUPLICATE_PAGE) {
    // Apply the page duplication again
    applyChangeToStore(change, get, set);
  } else if (change.type === ChangeType.DELETE_PAGE) {
    // Apply the page deletion again
  } else if (change.type === ChangeType.ADD_LIBRARY_COMPONENT) {
    applyChangeToStore(change, get, set);
  } else if (change.type === ChangeType.REMOVE_LIBRARY_COMPONENT) {
    applyChangeToStore(change, get, set);
  }
};

const wasChangeInstant = async (change: AnyChangeLedgerItem) => {
  if (!CHANGE_TYPES_WITH_INSTANT_UPDATE.includes(change.type)) {
    return false;
  }

  while (!change.getInstantUpdateSent()) {
    await new Promise((resolve) => setTimeout(resolve, 30));
  }

  return !change.instantUpdateFailed;
};

interface DesignCommandManagerStore {
  canvasId: string;
  // Queue of changes waiting for server update
  serverChangeQueue: AnyChangeLedgerItem[];
  // Queue of changes waiting for instant update
  instantChangeQueue: AnyChangeLedgerItem[];

  // History of all changes in the server queue
  serverChangeHistory: AnyChangeLedgerItem[];

  undoStack: AnyChangeLedgerItem[];
  redoStack: AnyChangeLedgerItem[];
  deletedActivityIds: Set<string>;

  setUndoStack: (undoStack: AnyChangeLedgerItem[]) => void;
  setRedoStack: (redoStack: AnyChangeLedgerItem[]) => void;

  // Whether we're currently processing a server change
  isProcessingServer: boolean;
  // Whether we're currently processing an instant change
  isProcessingInstant: boolean;

  // Clerk session
  session: SignedInSessionResource | null;

  // Submit a command that will be recorded a
  executeCommand: (designCommand: AnyChangeLedgerItem) => void;
  executeCommandAsPromise: (designCommand: AnyChangeLedgerItem) => Promise<any>;
  undo: () => void;
  redo: () => void;

  // Add a change to the instant queue
  sendChange: (change: AnyChangeLedgerItem) => void;
  sendInstantChange: (change: AnyChangeLedgerItem) => void;

  // Process the next server change
  processNextServerChange: () => Promise<void>;
  // Process the next instant change
  processNextInstantChange: () => Promise<void>;
  // Reset the store to initial state
  resetDesignCommandManagerStore: (canvasId: string) => void;

  // Set the clerk session
  setSession: (session: SignedInSessionResource | null) => void;

  isFlushing: boolean;
  setIsFlushing: (isFlushing: boolean) => void;

  // Editting classes by codebaseId, this is the source of truth, all edits happens here and are propagated
  // to backend/instant updates
  editingClassesByCodebaseId: {
    [codebaseId: string]: {
      value: string[];
      lastManualUpdateAt: Date | null;
    };
  };

  setEditingClassesByCodebaseId: (newEditingClassesByCodebaseId: {
    [codebaseId: string]: {
      value: string[];
      lastManualUpdateAt: Date | null;
    };
  }) => void;

  instantUpdateChannels: {
    [storyboardId: string]: (
      instantUpdateToApply: AnyChangeLedgerItem,
      useThisDoc?: Document,
    ) => void;
  };
  setInstantUpdateChannel: (
    storyboardId: string,
    channel?: (
      instantUpdateToApply: AnyChangeLedgerItem,
      useThisDoc?: Document,
    ) => void,
  ) => void;
}

export const useDesignCommandManager = create<DesignCommandManagerStore>(
  (set, get) => ({
    canvasId: "",
    isFlushing: false,
    serverChangeQueue: [],
    instantChangeQueue: [],
    serverChangeHistory: [],
    isProcessingServer: false,
    isProcessingInstant: false,
    emptyQueuesTimer: null,
    session: null,
    editingClassesByCodebaseId: {},
    undoStack: [],
    redoStack: [],
    deletedActivityIds: new Set(),
    instantUpdateChannels: {},

    setIsFlushing: (isFlushing: boolean) => {
      set({ isFlushing });
    },

    setUndoStack: (undoStack: AnyChangeLedgerItem[]) => {
      set({ undoStack });
    },

    setRedoStack: (redoStack: AnyChangeLedgerItem[]) => {
      set({ redoStack });
    },

    setEditingClassesByCodebaseId: (newEditingClassesByCodebaseId: {
      [codebaseId: string]: {
        value: string[];
        lastManualUpdateAt: Date;
      };
    }) => {
      set({ editingClassesByCodebaseId: newEditingClassesByCodebaseId });
    },

    setSession: (session: SignedInSessionResource | null) => {
      set({ session });
    },

    resetDesignCommandManagerStore: (canvasId: string) => {
      set({
        canvasId,
        serverChangeQueue: [],
        instantChangeQueue: [],
        serverChangeHistory: [],
        isProcessingServer: false,
        isProcessingInstant: false,
        editingClassesByCodebaseId: {},
        undoStack: [],
        redoStack: [],
      });
    },

    executeCommand: (change: AnyChangeLedgerItem) => {
      const allActivitiesInRedoStack = get()
        .redoStack.map((item) => item.activityId)
        .filter((id) => Boolean(id)) as string[];

      set((state) => ({
        undoStack: [...state.undoStack, change],
        redoStack: [],
        deletedActivityIds: new Set([
          ...state.deletedActivityIds,
          ...allActivitiesInRedoStack,
        ]),
      }));

      track(change.type);

      applyChangeToStore(change, get, set);
      get().sendChange(change);
    },

    executeCommandAsPromise: (change: AnyChangeLedgerItem): Promise<any> => {
      return new Promise((resolve, reject) => {
        change.onApiResolve(resolve);
        change.onApiReject(reject);
        get().executeCommand(change);
      });
    },

    undo: () => {
      const undoStack = [...get().undoStack];

      if (undoStack.length === 0) {
        return;
      }

      const changeToUndo = undoStack.pop()!;

      set((state) => ({
        redoStack: [...state.redoStack, changeToUndo],
        undoStack: undoStack,
      }));

      undoChangeInStore(changeToUndo, get, set);

      const changeToSend = new UndoChange({
        changeToUndo,
      });

      // Show progress toast for undo/redo
      const displayName = getDisplayNameForItem(
        changeToUndo,
        useActivityStore.getState().activityStreamList,
      );

      if (
        !CHANGE_TYPES_WITH_INSTANT_UPDATE.includes(changeToUndo.type) ||
        changeToUndo.instantUpdateFailed
      ) {
        const toastId = toast.loading(
          displayName ? `Undoing ${displayName}` : "Undoing Change",
        );
        changeToSend.toastId = toastId;

        setTimeout(() => {
          toast.dismiss(toastId);
        }, 10000);
      } else {
        toast.success(displayName ? `Undo: ${displayName}` : "Undo");
      }

      get().sendChange(changeToSend);
    },

    redo: () => {
      const redoStack = [...get().redoStack];
      if (redoStack.length === 0) {
        return;
      }

      const changeToRedo = redoStack.pop()!;
      set((state) => ({
        undoStack: [...state.undoStack, changeToRedo],
        redoStack: redoStack,
      }));

      redoChangeInStore(changeToRedo, get, set);

      const changeToSend: AnyChangeLedgerItem = new RedoChange({
        changeToRedo,
      });

      const displayName = getDisplayNameForItem(
        changeToRedo,
        useActivityStore.getState().activityStreamList,
      );
      if (
        !CHANGE_TYPES_WITH_INSTANT_UPDATE.includes(changeToRedo.type) ||
        changeToRedo.instantUpdateFailed
      ) {
        const toastId = toast.loading(
          displayName ? `Redoing ${displayName}` : "Redoing Change",
        );
        changeToSend.toastId = toastId;

        setTimeout(() => {
          toast.dismiss(toastId);
        }, 10000);
      } else {
        toast.success(displayName ? `Redo: ${displayName}` : "Redo");
      }

      get().sendChange(changeToSend);
    },

    sendChange: (change: AnyChangeLedgerItem) => {
      const newServerChangeQueue = [...get().serverChangeQueue, change];
      const newServerChangeHistory = [...get().serverChangeHistory, change];

      const newInstantChangeQueue: AnyChangeLedgerItem[] = [
        ...get().instantChangeQueue,
      ];
      newInstantChangeQueue.push(change);

      set({
        serverChangeQueue: newServerChangeQueue,
        serverChangeHistory: newServerChangeHistory,
        instantChangeQueue: newInstantChangeQueue,
      });

      // Start processing both queues if they're not already processing
      if (!get().isProcessingInstant) {
        get().processNextInstantChange();
      }
      if (!get().isProcessingServer) {
        get().processNextServerChange();
      }
    },

    sendInstantChange: (change) => {
      set((state) => ({
        instantChangeQueue: [...state.instantChangeQueue, change],
      }));

      if (!get().isProcessingInstant) {
        get().processNextInstantChange();
      }
    },

    processNextServerChange: async () => {
      const state = get();
      if (state.isProcessingServer || state.serverChangeQueue.length === 0) {
        return;
      }

      set({ isProcessingServer: true });
      const nextChange = state.serverChangeQueue[0];

      const { canvasId, session } = get();
      const { treeElementLookup } = useCodebaseStore.getState();

      const changesInGroup: AnyChangeLedgerItem[] = [];

      const afterChangeProcessed = (failed?: boolean) => {
        // Remove the processed change from the server queue
        set((state) => ({
          serverChangeQueue: state.serverChangeQueue.slice(
            changesInGroup.length,
          ),
          isProcessingServer: false,
        }));

        changesInGroup.forEach((change) => {
          if (change.toastId) {
            toast.dismiss(change.toastId);
            change.toastId = undefined;
          }

          if (failed) {
            change.markProcessedFailed();
          } else {
            change.markProcessedSucceeded();
          }
        });

        useActivityStore.getState().triggerRefetchActivityStream();

        // Process next server change if any
        if (get().serverChangeQueue.length > 0) {
          get().processNextServerChange();
        }
      };

      try {
        let changeToCheck = nextChange;
        if (
          nextChange.type === ChangeType.UNDO ||
          nextChange.type === ChangeType.REDO
        ) {
          changeToCheck =
            (nextChange as UndoChange).changeFields.changeToUndo ||
            (nextChange as RedoChange).changeFields.changeToRedo;
        }

        // Submit to server
        // Note: The actual server submission should be handled by the change item's implementation
        const request = nextChange.prepareApiRequest(
          canvasId,
          treeElementLookup,
        );

        if (changeToCheck.noApiRequest || !request) {
          // Do not process the change as no API request is needed
          changesInGroup.push(nextChange);
          afterChangeProcessed();
          return;
        }

        let urlPath = request.urlPath;
        const body = request.body ?? {};

        let expectedStepsCount = 0;
        if (
          nextChange.type === ChangeType.UNDO ||
          nextChange.type === ChangeType.REDO
        ) {
          // Group with other undo/redo changes in the server queue
          let changeToGroupWith = nextChange;
          let activityIdToRestoreTo: string | undefined;

          let i = 0;
          while (
            changeToGroupWith &&
            (changeToGroupWith.type === ChangeType.UNDO ||
              changeToGroupWith.type === ChangeType.REDO)
          ) {
            changesInGroup.push(changeToGroupWith);

            const realChange =
              (changeToGroupWith as UndoChange).changeFields.changeToUndo ??
              (changeToGroupWith as RedoChange).changeFields.changeToRedo;

            // Just skip to the next one
            if (realChange.noApiRequest) {
              changeToGroupWith = state.serverChangeQueue[i + 1];
              i++;
              continue;
            }

            if (changeToGroupWith.type === ChangeType.REDO) {
              activityIdToRestoreTo = (changeToGroupWith as RedoChange)
                .changeFields.changeToRedo.activityId;
            } else {
              activityIdToRestoreTo = (changeToGroupWith as UndoChange)
                .changeFields.changeToUndo.activityId;
            }

            if (changeToGroupWith.type === nextChange.type) {
              expectedStepsCount++;
            } else {
              expectedStepsCount--;
            }

            // If any of the changes in the group are not instant, just make the whole thing not instant
            const wasInstant = await wasChangeInstant(realChange);
            if (body.isInstantUpdate === undefined) {
              body.isInstantUpdate = wasInstant;
            } else {
              body.isInstantUpdate = body.isInstantUpdate && wasInstant;
            }

            changeToGroupWith = state.serverChangeQueue[i + 1];
            i++;
          }

          // Changes cancel out, no need to send to server
          if (!expectedStepsCount) {
            afterChangeProcessed();
            return;
          }

          // Flip the change type if the expected steps count is negative
          let overallChangeType = nextChange.type;
          if (expectedStepsCount < 0) {
            overallChangeType =
              nextChange.type === ChangeType.UNDO
                ? ChangeType.REDO
                : ChangeType.UNDO;
            expectedStepsCount = Math.abs(expectedStepsCount);
          }

          if (overallChangeType === ChangeType.REDO) {
            if (
              changesInGroup[changesInGroup.length - 1].type !==
              overallChangeType
            ) {
              // Last change in the group was an undo, so we need to redo to the activity before this one
              body.redoBeforeActivityId = activityIdToRestoreTo;
            } else {
              body.redoRestoreActivityId = activityIdToRestoreTo;
            }

            urlPath = `canvases/${canvasId}/parseAndMutate/activities/redoChangeToFiles`;
          } else {
            if (
              changesInGroup[changesInGroup.length - 1].type !==
              overallChangeType
            ) {
              body.undoRestoreActivityId = activityIdToRestoreTo;
            } else {
              body.undoBeforeThisActivityId = activityIdToRestoreTo;
            }

            urlPath = `canvases/${canvasId}/parseAndMutate/activities/undoChangeToFiles`;
          }
        } else {
          body.isInstantUpdate = await wasChangeInstant(nextChange);

          expectedStepsCount == 1;
          changesInGroup.push(nextChange);

          if (nextChange.asUndoOfActivityId) {
            body.asUndoOfActivityId = nextChange.asUndoOfActivityId;
          } else if (nextChange.asRedoOfActivityId) {
            body.asRedoOfActivityId = nextChange.asRedoOfActivityId;
          } else {
            body.activityId = nextChange.activityId;
          }
        }

        body.expectedStepsCount = expectedStepsCount;

        const supabaseJWT = await session?.getToken();
        const response = await axios.post(
          `${import.meta.env.VITE_BACKEND_BASE_URL}/${urlPath}`,
          body,
          {
            headers: {
              Authorization: `Bearer ${supabaseJWT}`,
            },
          },
        );

        if (
          nextChange.type === ChangeType.UNDO ||
          nextChange.type === ChangeType.REDO
        ) {
          useDatabaseSyncWrapperStore.getState().triggerRefetchCanvas();
        }

        if (response?.data) {
          useCodebaseStore
            .getState()
            .updateLookupsBasedOnPartialResponse(response.data);
        }

        if (response?.data?.warning) {
          toast.warning(response.data.warning);
        } else if (request.successToastMessage) {
          toast.success(request.successToastMessage);
        }

        changesInGroup.forEach((change) => {
          if (change.toastId) {
            toast.success("Success", { id: change.toastId });
            change.toastId = undefined;
          }

          change.resolveApi?.(response.data);

          // Set the hot reload timestamp from tempo-api response
          if (response?.data?.hotReloadTs) {
            change.hotReloadTs = response.data.hotReloadTs;
          }

          // Update pending page activity with actual updated_at from backend
          // This is used to prevent "bouncing" on rapid storyboard moves
          if (response?.data?.changedPages?.length > 0 && change.activityId) {
            const pagesStore = usePagesStore.getState();
            for (const changedPage of response.data.changedPages) {
              if (changedPage.id && changedPage.updated_at) {
                pagesStore.setPendingPageActivityUpdatedAt(
                  changedPage.id,
                  change.activityId,
                  changedPage.updated_at,
                );
              }
            }
          }
        });

        afterChangeProcessed(false);
      } catch (error) {
        console.error("Error processing server change:", error);

        // Apply the opposite instant update for the changes in the group (in reverse order)
        for (let i = changesInGroup.length - 1; i >= 0; i--) {
          const change = changesInGroup[i];
          if (change.type === ChangeType.UNDO) {
            get().sendInstantChange(change.changeFields.changeToUndo);
          } else if (change.type === ChangeType.REDO) {
            get().sendInstantChange(
              new UndoChange({
                changeToUndo: change.changeFields.changeToRedo,
              }),
            );
          } else {
            get().sendInstantChange(
              new UndoChange({
                changeToUndo: change,
              }),
            );
          }
        }

        // If this was an undo/redo, the activity stream is out of sync, reset the ledger to the activity stream
        if (
          nextChange.type === ChangeType.UNDO ||
          nextChange.type === ChangeType.REDO
        ) {
          resetStacksToActivityStream();
        }

        // Handle other errors normally
        toast.error("Error: " + getAxiosErorrMessage(error));

        // Trigger activity stream refetch immediately
        useActivityStore.getState().triggerRefetchActivityStream();

        const newUndoStack = [...get().undoStack];
        const newRedoStack = [...get().redoStack];
        changesInGroup.forEach((change) => {
          if (change.toastId) {
            toast.error("Error", { id: change.toastId });
            change.toastId = undefined;
          }

          // Put the change back into the undo/redo stack if it's an undo/redo change
          if (change.type === ChangeType.UNDO) {
            newRedoStack.pop();
            newUndoStack.push((change as UndoChange).changeFields.changeToUndo);
          } else if (change.type === ChangeType.REDO) {
            newUndoStack.pop();
            newRedoStack.push((change as RedoChange).changeFields.changeToRedo);
          }

          change.hotReloadTs = FAILED_CONSTANT;

          change.rejectApi?.(error);
        });

        set({
          undoStack: newUndoStack,
          redoStack: newRedoStack,
        });

        afterChangeProcessed(true);
      }
    },

    processNextInstantChange: async () => {
      const state = get();
      if (state.isProcessingInstant || state.instantChangeQueue.length === 0) {
        return;
      }

      set({ isProcessingInstant: true });
      const nextChange = state.instantChangeQueue[0];

      try {
        for (const channel of Object.values(state.instantUpdateChannels)) {
          channel(nextChange);
          if (!nextChange.hotReloadTs) {
            // Constant for applied but not confirmed by API yet
            nextChange.hotReloadTs = APPLIED_BUT_NOT_CONFIRMED_BY_API_CONSTANT;
          }
        }

        nextChange.markInstantUpdateSent();
      } catch (error) {
        console.error("Error processing instant change:", error);
      } finally {
        // Remove the processed change from the instant queue
        set((state) => ({
          instantChangeQueue: state.instantChangeQueue.slice(1),
          isProcessingInstant: false,
        }));

        // Process next instant change if any
        if (get().instantChangeQueue.length > 0) {
          get().processNextInstantChange();
        }
      }
    },

    setInstantUpdateChannel: (
      storyboardId: string,
      channel: (instantUpdateToApply: AnyChangeLedgerItem) => void,
    ) => {
      const newInstantUpdateChannels = { ...get().instantUpdateChannels };

      if (!channel) {
        delete newInstantUpdateChannels[storyboardId];
      } else {
        newInstantUpdateChannels[storyboardId] = channel;
      }

      set({
        instantUpdateChannels: newInstantUpdateChannels,
      });
    },
  }),
);

export const useEditingClassesByCodebaseId = (
  classList: string[],
  codebaseId: string,
) => {
  const editingClassesByCodebaseId = useDesignCommandManager(
    (state) => state.editingClassesByCodebaseId,
  );
  const setEditingClassesByCodebaseId = useDesignCommandManager(
    (state) => state.setEditingClassesByCodebaseId,
  );

  useEffect(() => {
    if (!codebaseId) {
      return;
    }

    const editingClassesByCodebaseId =
      useDesignCommandManager.getState().editingClassesByCodebaseId;

    const currentValue:
      | { value: string[]; lastManualUpdateAt: Date | null }
      | undefined = editingClassesByCodebaseId[codebaseId];

    if (!currentValue || !currentValue.lastManualUpdateAt) {
      // Set the classes to be the same as in the code
      useDesignCommandManager.getState().setEditingClassesByCodebaseId({
        [codebaseId]: {
          value: classList || [],
          lastManualUpdateAt: null,
        },
      });
    } else if (
      currentValue.lastManualUpdateAt < new Date(Date.now() - 10_000)
    ) {
      // If the classes have not been manually editted in the last 10 seconds, reset them to be the same as in the code
      useDesignCommandManager.getState().setEditingClassesByCodebaseId({
        ...editingClassesByCodebaseId,
        [codebaseId]: {
          value: classList || [],
          lastManualUpdateAt: null,
        },
      });
    }
  }, [classList, codebaseId]);

  if (!classList || !codebaseId) {
    return {
      classes: [],
      setClasses: () => {},
    };
  }

  const classes = editingClassesByCodebaseId[codebaseId]
    ? editingClassesByCodebaseId[codebaseId].value
    : classList || [];
  return {
    classes,
    setClasses: (classes: string[]) => {
      setEditingClassesByCodebaseId({
        ...editingClassesByCodebaseId,
        [codebaseId]: {
          value: classes,
          lastManualUpdateAt: new Date(),
        },
      });
    },
  };
};

export const useIsChangePending = () => {
  const isFlushing = useDesignCommandManager((state) => state.isFlushing);
  const serverChangeQueue = useDesignCommandManager(
    (state) => state.serverChangeQueue,
  );
  const instantChangeQueue = useDesignCommandManager(
    (state) => state.instantChangeQueue,
  );
  const isProcessingServer = useDesignCommandManager(
    (state) => state.isProcessingServer,
  );
  const isProcessingInstant = useDesignCommandManager(
    (state) => state.isProcessingInstant,
  );
  return (
    isFlushing ||
    serverChangeQueue.length > 0 ||
    instantChangeQueue.length > 0 ||
    isProcessingServer ||
    isProcessingInstant
  );
};

export const resetStacksToActivityStream = () => {
  const activityStreamList = useActivityStore.getState().activityStreamList;

  // Activity stream list is from newest to oldest
  // We pop off the end of the redo or undo stack to get the next activity to undo/redo
  const newRedoStack = activityStreamList
    .filter((activity) => activity.change_undone)
    .map((activity) => {
      const change = new UnknownChange({});
      change.activityId = activity.id;
      return change;
    });
  // Activity stream is newest to oldest, but undo stack should have newest at the END
  // so when we pop() we get the newest activity first
  const newUndoStack = activityStreamList
    .filter((activity) => !activity.change_undone)
    .reverse() // Reverse so newest activities are at the end for undoing
    .map((activity) => {
      const change = new UnknownChange({});
      change.activityId = activity.id;
      return change;
    });

  useDesignCommandManager.getState().setRedoStack(newRedoStack);
  useDesignCommandManager.getState().setUndoStack(newUndoStack);
};
